import { useState, useEffect } from 'react'
import { performanceService } from '@/services/api/performanceService'
import templateService from '@/services/api/templateService'
import toast from 'react-hot-toast'
import { mapTemplateToObjectives } from '@/components/performance/manager/objective-config/templateAllocation'
import { countDatedOutside, datesMovedNotice } from '@/lib/objectiveYear'

interface UseObjectiveAllocationOptions {
  teamMember: { employee_id: string; display_name: string } | null
  effectiveYear: number
  onAllocate: (objectives: any[]) => Promise<void>
  // After objectives were copied in on the server (from the year before or the Objective
  // Library): the parent sets up the meeting about them, as onAllocate does
  onObjectivesAdded?: () => Promise<void>
  onObjectivesChange?: () => Promise<void>
  setFullTeamMember: (member: any) => void
  setActiveTab: (tab: string) => void
  // Current manager id — used to surface own templates (any status) alongside published ones
  managerId?: string
  // Runtime lookups from API (environment-safe, unlike hardcoded maps)
  strategicObjectivesLookup?: Array<{ id: number; code: string; name: string; display_name: string }>
  strategicInitiativesLookup?: Array<{ id: number; code: string; name: string }>
  operationalInitiativesLookup?: Array<{ id: number; code: string; name: string }>
}

export function useObjectiveAllocation({
  teamMember,
  effectiveYear,
  onAllocate,
  onObjectivesAdded,
  onObjectivesChange,
  setFullTeamMember,
  setActiveTab,
  managerId,
  strategicObjectivesLookup = [],
  strategicInitiativesLookup = [],
  operationalInitiativesLookup = [],
}: UseObjectiveAllocationOptions) {
  const [showObjectiveWizard, setShowObjectiveWizard] = useState(false)
  const [allocationSource, setAllocationSource] = useState<'scratch' | 'template' | 'prior_year' | 'strategic' | null>(null)
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null)
  const [expandedTemplateObjective, setExpandedTemplateObjective] = useState<number | null>(null)
  const [allocatingTemplate, setAllocatingTemplate] = useState(false)

  useEffect(() => {
    const fetchTemplates = async () => {
      if (!teamMember || allocationSource !== 'template' || availableTemplates.length > 0) return

      try {
        // Single consolidated template source. Managers may allocate from their own
        // templates (any status, incl. draft) OR any published template.
        const response = await performanceService.getAllTemplates({ limit: 100 })
        const all = response.items || []
        const filtered = all.filter((t: any) =>
          (managerId && t.created_by === managerId) || t.is_published === true
        )
        setAvailableTemplates(filtered)
      } catch (error) {
        console.error('Error fetching templates:', error)
        toast.error('Failed to load templates')
      }
    }
    fetchTemplates()
  }, [allocationSource, availableTemplates.length, teamMember, managerId])

  const handleAllocateTemplate = async () => {
    if (!selectedTemplate || !teamMember) {
      toast.error('No template or team member selected')
      return
    }

    const { objectives: objectivesToAllocate, error } = mapTemplateToObjectives(selectedTemplate, {
      strategicObjectivesLookup,
      strategicInitiativesLookup,
      operationalInitiativesLookup,
    })
    if (error) {
      toast.error(error)
      return
    }

    // The writer dates every objective inside the year on screen (HR UAT list #27)
    const movedDates = countDatedOutside(effectiveYear, objectivesToAllocate)

    setAllocatingTemplate(true)
    try {
      toast.loading(`Adding ${objectivesToAllocate.length} objective(s)...`, { id: 'template-allocate' })

      await onAllocate(objectivesToAllocate)

      // Record template usage — allocation already succeeded, so failure here only logs
      try {
        const updated = await templateService.incrementUsage(selectedTemplate.id)
        setAvailableTemplates(prev => prev.map(t =>
          t.id === updated.id ? { ...t, usage_count: updated.usage_count, last_used_at: updated.last_used_at } : t
        ))
      } catch (usageError) {
        console.warn('Failed to increment template usage:', usageError)
      }

      toast.success(
        `Added ${objectivesToAllocate.length} objective(s) from template.` + datesMovedNotice(effectiveYear, movedDates),
        { id: 'template-allocate', duration: movedDates ? 6000 : 4000 }
      )

      setSelectedTemplate(null)
      setAllocationSource(null)

      if (onObjectivesChange) {
        await onObjectivesChange()
      }

      try {
        const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })
        const updatedMember = {
          ...teamMember,
          pending_objectives: data.objectives.filter((obj: any) => obj.status === 'pending_approval'),
          active_objectives: data.objectives.filter((obj: any) => obj.status === 'active'),
          rejected_objectives: data.objectives.filter((obj: any) => obj.status === 'rejected')
        }
        setFullTeamMember(updatedMember)
      } catch (err) {
        console.error('Error refreshing objectives:', err)
      }

      setActiveTab('pending')
    } catch (error: any) {
      console.error('Template allocation error:', error)
      toast.error(error.message || 'Failed to allocate template objectives', { id: 'template-allocate' })
    } finally {
      setAllocatingTemplate(false)
    }
  }

  const handleAllocateObjectives = async (objectives: any[]) => {
    try {
      await onAllocate(objectives)
      setShowObjectiveWizard(false)

      if (teamMember && onObjectivesChange) {
        await onObjectivesChange()
      }

      if (teamMember) {
        try {
          const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })
          const updatedMember = {
            ...teamMember,
            pending_objectives: data.objectives.filter((obj: any) => obj.status === 'pending_approval'),
            active_objectives: data.objectives.filter((obj: any) => obj.status === 'active'),
            rejected_objectives: data.objectives.filter((obj: any) => obj.status === 'rejected')
          }
          setFullTeamMember(updatedMember)
        } catch (err) {
          console.error('Error refreshing objectives after allocate:', err)
        }
      }

      setAllocationSource(null)
      setActiveTab('pending')
    } catch (error) {
      console.error('Error allocating objectives:', error)
    }
  }

  // Objectives copied in from the year before or the Objective Library (CopyObjectivesPanel):
  // the parent sets up the meeting about them, then the lists refresh as after any allocation
  const handleObjectivesCopied = async () => {
    try {
      await onObjectivesAdded?.()
      if (onObjectivesChange) await onObjectivesChange()
      if (teamMember) {
        const data = await performanceService.getEmployeeObjectives(teamMember.employee_id, { year: effectiveYear })
        setFullTeamMember({
          ...teamMember,
          pending_objectives: data.objectives.filter((obj: any) => obj.status === 'pending_approval'),
          active_objectives: data.objectives.filter((obj: any) => obj.status === 'active'),
          rejected_objectives: data.objectives.filter((obj: any) => obj.status === 'rejected')
        })
      }
    } catch (err) {
      console.error('Error refreshing objectives after copying them in:', err)
    }
    setAllocationSource(null)
    setActiveTab('pending')
  }

  const resetAllocationState = () => {
    setShowObjectiveWizard(false)
    setAllocationSource(null)
    setAvailableTemplates([])
    setSelectedTemplate(null)
    setExpandedTemplateObjective(null)
  }

  return {
    showObjectiveWizard,
    setShowObjectiveWizard,
    allocationSource,
    setAllocationSource,
    availableTemplates,
    selectedTemplate,
    setSelectedTemplate,
    expandedTemplateObjective,
    setExpandedTemplateObjective,
    allocatingTemplate,
    handleAllocateTemplate,
    handleAllocateObjectives,
    handleObjectivesCopied,
    resetAllocationState
  }
}
